import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Navigate, Route, Routes, useLocation } from 'react-router'
import {
  DepositLimit,
  PasswordConfirm,
  PersonalDetails,
  SecuritySettings,
} from '@mobi/account/Areas/SignUp/Components'
import {
  Passport,
  IdentityDocumentSelector,
  DriversLicence,
  Medicare,
} from '@mobi/account/Areas/SignUp/Components/Verification'
import { FailedVerification } from './Message/FailedVerification'
import { LogonRequestData, LogonResult } from '@mobi/api-types'
import { routes, SIGNUP_ROOT_ROUTE, subRoutes } from '../Utils/routes'
import { SignUpCompletePage } from './SignUpCompletePage'
import type { Campaign } from '../typings/types'
import type { Contract } from '@mobi/web-native-comms'
import { getSignUpState, setSignupNewSession } from '../Store'
import { isReactNativeApp } from '@mobi/web-native-comms/web'
import { useScrollRestoration } from '@mobi/account/Hooks/useScrollRestoration'

const postLoginRoutes: string[] = [routes.security, routes.depositLimit, routes.signUpComplete]

type SignUpPageProps = {
  isLoggedIn: boolean | null
  attemptLogin: (data: LogonRequestData) => Promise<
    LogonResult & {
      errorMessage?: string
    }
  >
  showLogin: (options?: { onLoginSuccessRedirectUrl?: string }) => void
  campaign: Campaign | undefined
  openDeposit: () => void
  trackOptimoveEvent: (event: Contract['OPTIMOVE_TRACK']) => void
  onMount: () => void
  onDispose: () => void
}

export const SignUpPage = ({
  isLoggedIn,
  attemptLogin,
  showLogin,
  campaign,
  openDeposit,
  trackOptimoveEvent,
  onMount,
  onDispose,
}: SignUpPageProps) => {
  const dispatch = useDispatch()
  const signUpState = useSelector(getSignUpState)
  const location = useLocation()
  const [newPassword, setNewPassword] = React.useState('')
  const isPostLoginRoute = postLoginRoutes.includes(location.pathname)

  useScrollRestoration()

  useEffect(() => {
    onMount()
    return () => {
      dispatch(setSignupNewSession())
      onDispose()
    }
  }, [])

  if (isLoggedIn && !isPostLoginRoute) {
    return <Navigate to={routes.home} />
  }

  return (
    <Routes>
      <Route path='/' element={<PersonalDetails trackOptimoveEvent={trackOptimoveEvent} />} />
      <Route path={subRoutes.verificationOptions} element={<IdentityDocumentSelector />} />
      <Route
        path={subRoutes.driversLicence}
        element={<DriversLicence trackOptimoveEvent={trackOptimoveEvent} />}
      />
      <Route
        path={subRoutes.passport}
        element={<Passport trackOptimoveEvent={trackOptimoveEvent} />}
      />
      <Route
        path={subRoutes.medicare}
        element={<Medicare trackOptimoveEvent={trackOptimoveEvent} />}
      />
      <Route
        path={subRoutes.password}
        element={
          <PasswordConfirm
            onCompleted={password => {
              setNewPassword(password)
            }}
            attemptLogin={attemptLogin}
            trackOptimoveEvent={trackOptimoveEvent}
          />
        }
      />
      <Route
        path={subRoutes.security}
        element={
          isReactNativeApp ? ( //TODO: remove this condition once we add MFA option since this page will no longer have a single native-only option
            <SecuritySettings
              betAccountNumber={signUpState?.newUserBetAccount}
              password={newPassword}
            />
          ) : (
            <Navigate to={routes.depositLimit} />
          )
        }
      />
      <Route
        path={subRoutes.signUpUnsuccessful}
        element={<FailedVerification showLogin={showLogin} />}
      />
      <Route
        path={subRoutes.signUpComplete}
        element={<SignUpCompletePage campaign={campaign} openDeposit={openDeposit} />}
      />
      <Route path={subRoutes.depositLimit} element={<DepositLimit />} />
      <Route path='*' element={<Navigate to={`/${SIGNUP_ROOT_ROUTE}`} />} />
    </Routes>
  )
}

SignUpPage.displayName = 'SignUpPage'

export default SignUpPage
